<script>
export default {
  name: 'OrderFavoriteDetail'
}
</script>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue';

import Pagination from '@parts/Pagination.vue';
import OrderNavigation from '@parts/OrderNavigation.vue';
import GoodsList from '@parts/GoodsList.vue';
import axios from 'axios';
import DeleteFromFavoriteCompleteModal from './modal/DeleteFromFavoriteCompleteModal.vue';
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import { modalUtil } from '@functions/modalUtil.js';
import FavoriteAddToCartConfirmModal from './modal/FavoriteAddToCartConfirmModal.vue';
import { url } from '@/config/urls';
import router from '@/router/index.js';
import { useRoute } from 'vue-router';


// 外部定義メソッド
const { showModal, switchFromCurrentModal } = modalUtil();
const route = useRoute();

// 画面ID
const screenId = ref("OrderFavoriteDetail");

// エラーメッセージの状態管理
const errorMessages = ref([]);

// 定数定義
const pageSizeArray = [50, 100, 200];

// URLパラメータからお気に入りIDを取得
const goodsFavId = computed(() => {
  const id = Number(route.query.id);
  return Number.isInteger(id) ? id : null;
});

// お気に入り名称
const goodsFavName = ref('');

// Prop定義
const props = defineProps({
  userDataUrl: {
    type: String,
    default: url.api.userData
  },
  orderFavoritePageUrl: {
    type: String,
    default: url.page.orderFavorite
  },
  orderFavoriteListApiUrl: {
    type: String,
    default: url.api.orderFavorite.list
  },
  orderFavoriteItemListApiUrl: {
    type: String,
    default: url.api.orderFavoriteItem.list
  },
  orderFavoriteItemDeleteApiUrl: {
    type: String,
    default: url.api.orderFavoriteItem.delete
  },
  orderCartCountApiUrl: {
    type: String,
    default: url.api.orderCart.count
  },
  orderCartAddApiUrl: {
    type: String,
    default: url.api.orderCart.add
  },
  goodsTypesApiUrl: {
    type: String,
    default: url.api.goodsTypes
  },
  loginUrl: {
    type: String,
    default: url.login
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  }
});

// ユーザー情報
const user = reactive({
  isPermittedOrder: null,  // null: 未取得, true: 発注, false: 在庫問合せ
  deliveryDateAllowedFlg: null  // null: 未取得, 1: 納品日指定可能, 0: 納品日指定不可
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedOrder = response.data.response.isPermittedOrder;
    user.deliveryDateAllowedFlg = response.data.response.deliveryDateAllowedFlg;
  } catch (error) {
    apiError(error, errorMessages);
  }
}


// 商品一覧取得APIリクエストパラメータ
const condition = ref({
  goodsFavId: goodsFavId,
  size: 50,
  page: 1
});


// お気に入り一覧に戻る
const backToOrderFavorite = () => {
  router.push(props.orderFavoritePageUrl);
}

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// お気に入りグループ
const goodsFavorites = ref([]);

// 検索結果
const goodsList = ref([]);
const totalNum = ref(0);
const goodsQuantityList = ref([]);

// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};

// カート商品 品目数
const cartGoodsTotalRecord = ref(0);
// カート商品集計
const countCartGoods = () => {
  axios.get(props.orderCartCountApiUrl)
    .then((response) => {
      cartGoodsTotalRecord.value = parseInt(response.data.response.totalNum);
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// お気に入りマスタデータ取得処理
const getGoodsFavorites = () => {
  axios.get(props.orderFavoriteListApiUrl)
    .then((response) => {
      goodsFavorites.value = response.data.response.goodsFavorites;
      
      // goodsFavIdと一致するお気に入りを検索してgoodsFavNameに設定
      const targetFavorite = goodsFavorites.value.find(favorite => favorite.id == goodsFavId.value);
      if (targetFavorite) {
        goodsFavName.value = targetFavorite.name;
      }
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品分類マスタデータ取得処理
const getGoodsTypes = () => {
  axios.get(props.goodsTypesApiUrl)
    .then((response) => {
      goodsTypes.value = response.data.response.goodsTypes;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品一覧取得処理
const getGoodsList = async () => {
  await axios.get(props.orderFavoriteItemListApiUrl, { params:condition.value })
    .then((response) => {
      if (response.data.response.goodsList) {
        goodsList.value = response.data.response.goodsList;
      } else {
        goodsList.value = [];
      }
      // 商品数量リストセット
      setGoodsQuantityList(response.data.response.janCodeList);
      
      totalNum.value = parseInt(response.data.response.totalNum);
      pagingParameter.value.pageNum = parseInt(response.data.response.currentPage);
      totalPage.value = Math.ceil(totalNum.value / pagingParameter.value.pageSize);
      totalPage.value = totalPage.value ? totalPage.value : 1;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    })
    .finally(() => {
      handleUpdateLoading(false);
    });
}

// 商品数量リストセット
const setGoodsQuantityList = (janCodeList) => {
  if (!Array.isArray(janCodeList)) {
    return;
  }

  goodsQuantityList.value = janCodeList.map(janCode => {
    const targetGoods = goodsList.value.find(g => g.janCode === janCode);
    return {
      janCode: janCode,
      quantity: targetGoods && targetGoods.canOrder ? 1: 0
    };
  });
};

// 商品数量リストの変更
const updateGoodsQuantityList = (updatedGoodsQuantityList) => {
  goodsQuantityList.value = updatedGoodsQuantityList;
}

// ページネーション処理
const onChangePageNum = (page) => {
  if (!Number.isFinite(page)) {
    return;
  }
  condition.value.page = page;
  getGoodsList();

  pagingParameter.value.pageNum = page;

  window.scroll({
      top: 0,
      behavior: 'smooth'
  });
}

const onChangePageSize = (selected) => {
  condition.value.page = 1;
  condition.value.size = selected;
  getGoodsList();

  pagingParameter.value.pageNum = 1;
  pagingParameter.value.pageSize = selected;

}

// すべてカートへ 重複確認
const handleClickFavoriteAddToCart = () => {
  const janCodeList = goodsQuantityList.value
  .filter(item => item.quantity && item.quantity > 0)
  .map(item => item.janCode);
  handleUpdateLoading(true);
  axios.get(props.orderCartCountApiUrl, { params: { janCodeList: janCodeList } })
    .then((response) => {
      if (response.data.response.totalNum === 0) {
        // 重複なしのため、警告なしで追加
        favoriteAddToCart();
      } else {
        // 重複ありのため、警告モーダルを表示
        handleUpdateLoading(false);
        showModal('favoriteAddToCartConfirmModal');
      }
    })
    .catch((error) => {
      handleUpdateLoading(false);
      apiError(error, errorMessages);
    });
}

// すべてカートへ 追加
const favoriteAddToCart = () => {
  const goodsList = goodsQuantityList.value
  .filter(item => item.quantity && item.quantity > 0)
  .map(item => ({
    janCode: item.janCode,
    quantity: item.quantity
  }));
  handleUpdateLoading(true);
  axios.post(props.orderCartAddApiUrl, {goodsList: goodsList})
    .then((response) => {
      switchFromCurrentModal('addToCartCompleteModal');
      countCartGoods();
    })
    .catch((error) => {
      apiError(error, errorMessages);
    })
    .finally(() => {
      // 数量をリセット
      goodsQuantityList.value.forEach(item => {
        item.quantity = 0;
      });
      handleUpdateLoading(false);
    });
}

// すべての商品数量が0かどうかの判定
const isAllQuantitiesZero = computed(() => {
  if (!goodsQuantityList.value || goodsQuantityList.value.length === 0) {
    return true;
  }
  return goodsQuantityList.value.every(item => item.quantity === 0);
});

const handleDeleteFromFavoriteComplete = async() => {
  handleUpdateLoading(true);
  await Promise.all([
    getGoodsList()
    , getGoodsFavorites()
  ]);
  handleUpdateLoading(false);
}
 // 初期処理
onMounted(async() => {
  handleUpdateLoading(true);
  await getUserInfo();
  if (!user.isPermittedOrder) {
    handleUpdateLoading(false);
    return;
  }

  await Promise.all([
    countCartGoods(),
    getGoodsFavorites(),
    getGoodsTypes(),
    getGoodsList()
  ]);

  handleUpdateLoading(false);
  // URLパラメータをチェックして削除完了モーダルを表示
  if (route.query.deleted === 'true') {
    // URLパラメータを削除
    const { deleted, ...restQuery } = route.query;
    router.replace({ query: restQuery });
    
    // 画面読み込み完了後にモーダル表示
    await nextTick(() => {
      setTimeout(() => {
        showModal('deleteFromFavoriteCompleteModal');
      }, 100);
    });
  }

});
</script>

<template>

  <div id="contents1" class="bg1" v-if="user.isPermittedOrder">
    <OrderNavigation
      :screenId = "screenId"
      :cartGoodsTotalRecord = cartGoodsTotalRecord
    ></OrderNavigation>

    <template v-if="goodsFavName === ''">
      <p class="com_center1 com_fs16">グループが見つかりません</p>
      <div class="com_center1 com_mgb20">
        <button type="button" class="btn com_btn2 wd1" @click="backToOrderFavorite">一覧へ戻る</button>
      </div>
    </template>
    <template v-else>
      <h2 class="com_title1 com_mgb5">{{ goodsFavName }}</h2>
      <template v-if="goodsList.length === 0">
        <p class="com_center1 com_fs16">登録されている商品はありません</p>
        <div class="com_center1 com_mgb20">
          <button type="button" class="btn com_btn2 wd1" @click="backToOrderFavorite">一覧へ戻る</button>
        </div>
      </template>
    </template>

    <div v-if="goodsList.length > 0" class="com_inner4 clearfix">

      <Pagination
        :isShowFirstAndLastBtn="true"
        :isShowPaginationInfo="true"
        :totalNum="totalNum"
        :currentPage="pagingParameter.pageNum"
        :totalPage="totalPage"
        :pageSize="pagingParameter.pageSize"
        :pageSizeSelectList="pageSizeArray"
        :screenId = "screenId"
        :isAllQuantitiesZero="isAllQuantitiesZero"
        @changePageNum="onChangePageNum"
        @changePageSize="onChangePageSize"
        @clickFavoriteAddToCart="handleClickFavoriteAddToCart"
      ></Pagination>

      <GoodsList
        :screenId = "screenId"
        :isPermittedOrder = user.isPermittedOrder
        :deliveryDateAllowedFlg = user.deliveryDateAllowedFlg
        :goodsList = goodsList
        :goodsTypes = goodsTypes
        :goodsFavorites = goodsFavorites
        :goodsFavId = goodsFavId
        :goodsQuantityList = goodsQuantityList
        @updateLoading="handleUpdateLoading"
        @countCartGoods="countCartGoods"
        @updateGoodsQuantityList="updateGoodsQuantityList"
        @apiError="handleApiError"
        @deleteFromFavoriteComplete="handleDeleteFromFavoriteComplete"
      ></GoodsList>

      <Pagination
        :isShowFirstAndLastBtn="true"
        :isShowPaginationInfo="true"
        :totalNum="totalNum"
        :currentPage="pagingParameter.pageNum"
        :totalPage="totalPage"
        :pageSize="pagingParameter.pageSize"
        :pageSizeSelectList="pageSizeArray"
        @changePageNum="onChangePageNum"
        @changePageSize="onChangePageSize"
      ></Pagination>

    </div>
  </div>

  <!-- カート追加重複確認モーダル -->
  <FavoriteAddToCartConfirmModal
    @favoriteAddToCart="favoriteAddToCart"
  ></FavoriteAddToCartConfirmModal>

  <!-- 削除完了モーダル -->
  <DeleteFromFavoriteCompleteModal
    :goodsFavName="goodsFavName"
    @hidden="handleDeleteFromFavoriteComplete"
  ></DeleteFromFavoriteCompleteModal>

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>